/**
 * Drilldown Header
 * Container for common record drilldown header items such as
 * title and back button
 */
import { Stack } from '@mui/material';
import { FormikProvider, useFormikContext } from 'formik';
import React from 'react';

import DotMenu from '@components/DotMenu';
import FavouriteStar from '@components/FavouriteStar';
import Icon from '@components/Icon/Icon';
import { ShareButton } from '@components/ShareButton';

import { useCurationMode } from '@contexts/CurationModeContext';

import { useAutoCuration } from '@hooks/useAutoCuration';
import { useCuration } from '@hooks/useCuration';
import { useSession } from '@hooks/useSession';

import type { FhirResource } from '@models/fhir-resource';

import { mapResourceTypeToHeading } from '@utils/mapBBoxTitleToFormType';

import { ShareType } from 'generated/types';

import AutoCurationDrilldownDeleteButton from './CurationComponents/AutoCurationDeleteButton';
import CurationDrilldownDeleteButton from './CurationComponents/DeleteButton';
import CurationDrilldownEditButton from './CurationComponents/EditButton';
import CurationDrilldownSourceType from './CurationComponents/SourceType';
import * as Default from './DrilldownHeader';
import { IconDiv } from './DrilldownHeader.style';

interface HeaderItems extends Default.HeaderItems {
  editingComponent?: React.ReactNode;
}

interface DrilldownHeaderProps extends Default.DrilldownHeaderProps {
  canFavourite?: boolean;
  id?: string | null;
  selfReported?: boolean;
  titleInput?: React.ReactNode | null;
  resource: FhirResource | null;
  loading?: boolean;
  hasNotes?: boolean;
  items?: HeaderItems[];
}

function DrilldownHeader({
  title = '',
  subtitle = '',
  items = [],
  backRoute = '',
  hasNotes = false,
  id = '',
  canFavourite = false,
  actions = [],
  selfReported = false,
  titleInput = null,
  resource,
  ...props
}: DrilldownHeaderProps) {
  const formik = useFormikContext<Record<string, unknown>>();

  const {
    state: { isCreating, isEditing, record },
  } = useCuration();
  const { curationMode } = useCurationMode();
  const {
    state: { isCurator, activeProfile },
  } = useSession();
  const {
    state: { currentRecord },
  } = useAutoCuration();
  const titleElement = React.useMemo(
    () => ((isCreating || isEditing) && titleInput ? titleInput : title),
    [isCreating, isEditing, title, titleInput]
  );
  return (
    <FormikProvider value={formik}>
      <Default.DrilldownHeader
        {...props}
        backRoute={isCreating ? undefined : backRoute}
        items={items.map(({ editingComponent, title, ...rest }) => ({
          ...rest,
          title:
            isCreating || isEditing ? (
              <Stack spacing={2.5}>{editingComponent}</Stack>
            ) : (
              title
            ),
        }))}
        title={titleElement}
        heading={
          curationMode
            ? mapResourceTypeToHeading(currentRecord?.formType || '')
            : ''
        }
        subtitle={subtitle}
        forceShowSubtitle={isCurator}
        preChildren={
          <CurationDrilldownSourceType
            resource={resource}
            selfReported={selfReported}
          />
        }
        buttonsElement={
          <React.Fragment>
            {hasNotes ? (
              <Icon
                clickable
                color="primary.contrastText"
                name="file-lines"
                type="fas"
              />
            ) : null}
            {canFavourite ? <FavouriteStar id={id} /> : null}
            {resource?.isShareable() && activeProfile?.vitall_patient_id && (
              <ShareButton
                resource_model_id={resource.getId()}
                vitall_patient_id={activeProfile.vitall_patient_id}
                share_type={ShareType.SingleResource}
              />
            )}
            {record ? (
              <IconDiv>
                {!curationMode ? (
                  <CurationDrilldownDeleteButton />
                ) : (
                  <AutoCurationDrilldownDeleteButton />
                )}
                <CurationDrilldownEditButton />
              </IconDiv>
            ) : actions.length ? (
              <DotMenu actions={[...actions]} />
            ) : null}
          </React.Fragment>
        }
      />
    </FormikProvider>
  );
}

export { DrilldownHeader };
export type { DrilldownHeaderProps, HeaderItems };
